import React, { useEffect, useRef, useState } from "react";
import { CreditCard, Send, Lock } from "lucide-react";
import { toast } from "sonner";
import { Card, PrimaryButton, EmptyState } from "../UI";
import portalApi, { errMsg } from "../../lib/portalApi";

export const PayTab = () => {
  const [bal, setBal] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { portalApi.get("/portal/balance").then(({ data }) => setBal(data)); }, []);
  const pay = async () => {
    setBusy(true);
    try {
      const { data } = await portalApi.post("/portal/payments/checkout", { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };
  if (!bal) return <div className="mf-skeleton h-40" />;
  return (
    <Card testid="portal-pay">
      <h3 className="font-serif text-2xl mb-1">Balance</h3>
      <div className="font-serif text-5xl my-4" data-testid="portal-balance-total">${bal.total.toFixed(2)}</div>
      {bal.items.length === 0 ? <EmptyState icon={CreditCard} title="You're all paid up" description="Thank you!" /> : (
        <>
          <div className="space-y-2 mb-5">
            {bal.items.map((i) => (
              <div key={i.id} className="p-3 rounded-xl flex justify-between text-sm" style={{ background: "var(--mf-muted)" }} data-testid={`portal-bill-${i.id}`}>
                <span>Session {i.date} · {i.cpt_code}</span><span className="font-medium">${Number(i.amount).toFixed(2)}</span>
              </div>
            ))}
          </div>
          <PrimaryButton onClick={pay} disabled={busy} testid="portal-pay-btn"><span className="flex items-center gap-2"><Lock className="w-4 h-4" /> {busy ? "Opening secure checkout…" : `Pay $${bal.total.toFixed(2)}`}</span></PrimaryButton>
          <p className="text-xs mt-3" style={{ color: "var(--mf-text-muted)" }}>Payments are processed securely by Stripe. Test mode: use card 4242 4242 4242 4242.</p>
        </>
      )}
      {bal.payments.length > 0 && (
        <div className="mt-6">
          <div className="text-sm font-medium mb-2">Past payments</div>
          {bal.payments.map((p) => <div key={p.session_id} className="text-xs py-1" style={{ color: "var(--mf-text-muted)" }}>{new Date(p.created_at).toLocaleDateString()} · ${p.amount.toFixed(2)}</div>)}
        </div>
      )}
    </Card>
  );
};

export const MessagesTab = ({ me }) => {
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const end = useRef(null);
  const load = () => portalApi.get("/portal/messages").then(({ data }) => setMsgs(data));
  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, []);
  useEffect(() => { end.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);
  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    try { await portalApi.post("/portal/messages", { body: text }); setText(""); load(); }
    catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <Card testid="portal-messages">
      <h3 className="font-serif text-2xl mb-1">Secure messages</h3>
      <p className="text-xs mb-4 flex items-center gap-1" style={{ color: "var(--mf-text-muted)" }}><Lock className="w-3 h-3" /> Private between you and {me.clinician_name || "your clinician"}. Not for emergencies — call 988 or 911.</p>
      <div className="h-[50vh] overflow-y-auto space-y-2 p-3 rounded-xl" style={{ background: "var(--mf-muted)" }} data-testid="portal-thread">
        {msgs.length === 0 && <div className="text-sm text-center py-10" style={{ color: "var(--mf-text-muted)" }}>No messages yet. Say hello.</div>}
        {msgs.map((m) => (
          <div key={m.id} className={`flex ${m.sender === "client" ? "justify-end" : "justify-start"}`}>
            <div className="max-w-[80%] px-4 py-2.5 rounded-2xl text-sm whitespace-pre-wrap" style={{ background: m.sender === "client" ? "var(--mf-primary)" : "var(--mf-surface)", color: m.sender === "client" ? "#FAFAF8" : "var(--mf-text)" }} data-testid="portal-msg">
              {m.body}
              <div className="text-[10px] mt-1 opacity-60">{new Date(m.created_at).toLocaleString([], { dateStyle: "short", timeStyle: "short" })}</div>
            </div>
          </div>
        ))}
        <div ref={end} />
      </div>
      <form onSubmit={send} className="flex gap-2 mt-3">
        <input value={text} onChange={(e) => setText(e.target.value)} maxLength={5000} placeholder="Write a message…" className="flex-1 px-4 py-2.5 rounded-full text-sm outline-none" style={{ background: "var(--mf-muted)" }} data-testid="portal-msg-input" />
        <button type="submit" className="mf-btn-primary px-4" aria-label="Send" data-testid="portal-msg-send"><Send className="w-4 h-4" /></button>
      </form>
    </Card>
  );
};

export const PrefsTab = ({ me, setMe }) => {
  const toggle = async (key) => {
    try {
      const { data } = await portalApi.put("/portal/preferences", { reminder_email: me.reminder_email, reminder_sms: me.reminder_sms, [key]: !me[key] });
      setMe(data);
      toast.success("Preferences saved");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const Row = ({ k, label, desc }) => (
    <button onClick={() => toggle(k)} className="w-full flex items-center justify-between p-4 rounded-xl text-left" style={{ background: "var(--mf-muted)" }} data-testid={`portal-pref-${k}`}>
      <span><span className="block text-sm font-medium">{label}</span><span className="block text-xs" style={{ color: "var(--mf-text-muted)" }}>{desc}</span></span>
      <span className="w-11 h-6 rounded-full relative transition-colors" style={{ background: me[k] ? "var(--mf-primary)" : "#ccc" }}>
        <span className="absolute top-0.5 w-5 h-5 bg-white rounded-full transition-transform" style={{ transform: me[k] ? "translateX(22px)" : "translateX(2px)" }} />
      </span>
    </button>
  );
  return (
    <Card testid="portal-prefs">
      <h3 className="font-serif text-2xl mb-4">Reminder preferences</h3>
      <div className="space-y-2">
        <Row k="reminder_email" label="Email reminders" desc={`Sent to ${me.email} 24 hours and 2 hours before sessions`} />
        <Row k="reminder_sms" label="Text message reminders" desc={me.phone ? `Sent to ${me.phone}. Msg & data rates may apply. Reply STOP to opt out.` : "Ask your clinician to add your mobile number"} />
      </div>
    </Card>
  );
};
